import * as React from 'react';

import Select, { CSSObjectWithLabel, StylesConfig } from 'react-select';
import { DefaultSelectProps, ISelectOption, UskinnedSelectStyles } from '../models/select';
import { ObjectUtility } from '../utilities/object';

import { StringUtility } from '../utilities/string';

const MinuteOptions = [];
const HourOptions = [];
const SecondOptions = [];
const TOTAL_HOURS = 24;
const TOTAL_MINUTES = 60;

for (let unit = 0; unit <= TOTAL_MINUTES; unit++) {
    const unitString = StringUtility.addZero(unit, 2);
    const option = {
        label: unitString,
        value: unitString
    };
    if (unit < TOTAL_HOURS) {
        HourOptions.push(option);
    }
    MinuteOptions.push(option);
    SecondOptions.push(option);
}

const timePickerStyles: StylesConfig = {
    ...UskinnedSelectStyles,
    container: (provided: CSSObjectWithLabel) => ({
        ...provided,
        display: 'inline-block'
    })
};

export interface ITimePickerDefaultValue {
    hour: string;
    minute: string;
}

interface ITimePickerProps {
    defaultValue?: ITimePickerDefaultValue;
    onChange?: (value: string) => void;
    disabled?: boolean;
}

interface ITimePickerComponentProps extends Omit<DefaultSelectProps, 'defaultValue'> {
    defaultValue?: string;
    options: Array<{
        label: string;
        value: string;
    }>;
}

const TimePickerComponent = (props: ITimePickerComponentProps) => {
    const { options, defaultValue } = props;
    return (
        <Select
            {...props}
            {...(defaultValue
                ? {
                    defaultValue: options.filter(option => option.value === defaultValue + '')[0]
                }
                : {})}
            styles={timePickerStyles}
            isClearable={true}
        />
    );
};

const isValidTime = (hour: string, minute: string): boolean => {
    if (!hour || !minute) {
        return false;
    }
    if (hour.length !== 2 || minute.length !== 2) {
        return false;
    }
    const numericHour = Number(hour);
    const numericMinute = Number(minute);
    if (isNaN(numericHour) || isNaN(numericMinute)) {
        return false;
    }
    if (numericHour < 0 || numericHour >= TOTAL_HOURS) {
        return false;
    }
    if (numericMinute < 0 || numericMinute >= TOTAL_MINUTES) {
        return false;
    }
    return true;
};
const toTime = (hour: string, minute: string): string => {
    const parsedHour = StringUtility.addZero(hour, 2);
    const parsedMinute = StringUtility.addZero(minute, 2);
    return `${parsedHour}:${parsedMinute}`;
};

export const TimePicker = ({ defaultValue, onChange, disabled = false }: ITimePickerProps) => {
    const defaultTime = React.useMemo(() => {
        const hasDefaultValue = defaultValue && defaultValue.hour && defaultValue.minute;
        if (!hasDefaultValue) {
            return '';
        }
        const { hour, minute } = defaultValue || {};
        const timeStr = toTime(hour, minute);
        const isValidDefaultValue = isValidTime(hour, minute);
        if (!isValidDefaultValue) {
            return '';
        }
        return timeStr;
    }, [defaultValue?.hour, defaultValue?.minute]);

    const getDefaultValue = React.useCallback(
        (type: 'hour' | 'minute') => {
            if (!defaultTime) {
                return {};
            }
            const value = defaultValue[type];
            if (!ObjectUtility.isNil(value)) {
                return {
                    defaultValue: value
                };
            }
            return {};
        },
        [defaultTime]
    );
    const [fullTime, setFullTime] = React.useState(defaultTime);
    const [tempHour, setTempHour] = React.useState(defaultTime ? defaultValue.hour : '');
    const [tempMinute, setTempMinute] = React.useState(defaultTime ? defaultValue.minute : '');

    React.useEffect(() => {
        const currentTime = toTime(tempHour, tempMinute);
        if (!isValidTime(tempHour, tempMinute)) {
            setFullTime('');
            return;
        }
        setFullTime(currentTime);
    }, [tempHour, tempMinute]);

    React.useEffect(() => {
        onChange(fullTime);
    }, [fullTime]);

    return (
        <>
            {/* Hour - 0 to 23 */}
            <TimePickerComponent
                options={HourOptions}
                {...getDefaultValue('hour')}
                placeholder='Hour'
                className='mr-2'
                onChange={(newValue: ISelectOption) => {
                    if (!newValue) {
                        setTempHour('');
                        return;
                    }
                    setTempHour(newValue.value);
                }}
                isDisabled={disabled}
            />
            {/* Minute - 0 to 59 */}
            <TimePickerComponent
                options={MinuteOptions}
                {...getDefaultValue('minute')}
                placeholder='Minute'
                className='mr-2'
                onChange={(newValue: ISelectOption) => {
                    if (!newValue) {
                        setTempMinute('');
                        return;
                    }
                    setTempMinute(newValue.value);
                }}
                isDisabled={disabled}
            />
        </>
    );
};
